import { formatDistanceToNow, parseISO } from 'date-fns';
import React from 'react';
import { HeadType, RowType } from '@atlaskit/dynamic-table/dist/types/types';
import { ApprovalStatus as ApprovalStatusType, Task, UserRole } from '@css-xp/apd-common';
import { FormattedDate } from '@css-xp/base';
import { ApprovalStatus, withTooltip } from '../../common';
import { statusToShow } from '../../common/status/status';
import { DisplayRequestType } from '../../constants/approvals';
import { TABLE_COLUMN_KEYS, TABLE_HEADER } from '../../constants/table';
import { TaskActionsList } from '../../task-actions/task-actions-list';
import { TaskDetailAction } from '../actions';
import './all-tasks-list.scss';

interface CreateTableRowsParam {
  taskslist: Task[];
  userRole: UserRole | undefined | null;
  refetch: () => void;
}

const getApprovalStatusContent = (status: ApprovalStatusType | undefined | null) => {
  if (!status) {
    return <></>;
  }
  return withTooltip(
    statusToShow[status] ? statusToShow[status].toUpperCase() : status,
    <ApprovalStatus status={status} />
  );
};

export const createTableHead = (withWidth: boolean): HeadType => {
  return {
    cells: [
      {
        key: TABLE_COLUMN_KEYS.CREATED_DATE,
        content: TABLE_HEADER.REQUEST_DATE,
        isSortable: false,
        shouldTruncate: true,
        width: withWidth ? 20 : undefined,
        testId: TABLE_COLUMN_KEYS.CREATED_DATE,
      },
      {
        key: TABLE_COLUMN_KEYS.TASK_NUMBER,
        content: TABLE_HEADER.TASK_DISPLAY_ID,
        isSortable: false,
        shouldTruncate: true,
        width: withWidth ? 20 : undefined,
        testId: TABLE_COLUMN_KEYS.TASK_NUMBER,
      },
      {
        key: TABLE_COLUMN_KEYS.GSAC_ID,
        content: TABLE_HEADER.GSAC_ID,
        isSortable: false,
        shouldTruncate: true,
        width: withWidth ? 20 : undefined,
        testId: TABLE_COLUMN_KEYS.GSAC_ID,
      },
      {
        key: TABLE_COLUMN_KEYS.REQUEST_TYPE,
        content: TABLE_HEADER.REQUEST_TYPE,
        isSortable: false,
        shouldTruncate: true,
        width: withWidth ? 20 : undefined,
        testId: TABLE_COLUMN_KEYS.REQUEST_TYPE,
      },
      {
        key: TABLE_COLUMN_KEYS.INITIATOR,
        content: TABLE_HEADER.INITIATOR,
        isSortable: false,
        shouldTruncate: true,
        width: withWidth ? 20 : undefined,
        testId: TABLE_COLUMN_KEYS.INITIATOR,
      },
      {
        key: TABLE_COLUMN_KEYS.APPROVER,
        content: TABLE_HEADER.OWNER_NAME,
        isSortable: false,
        shouldTruncate: true,
        width: withWidth ? 20 : undefined,
        testId: TABLE_COLUMN_KEYS.APPROVER,
      },
      {
        key: TABLE_COLUMN_KEYS.STATUS,
        content: TABLE_HEADER.STATUS,
        isSortable: false,
        shouldTruncate: true,
        width: withWidth ? 20 : undefined,
        testId: TABLE_COLUMN_KEYS.STATUS,
      },
      {
        key: TABLE_COLUMN_KEYS.ACTION,
        content: '',
        isSortable: false,
        shouldTruncate: true,
        width: withWidth ? 20 : undefined,
        testId: TABLE_COLUMN_KEYS.ACTION,
      },
    ],
  };
};

export const createTableRows = ({ taskslist, userRole, refetch }: CreateTableRowsParam): Array<RowType> => {
  return taskslist.map((task: Task, index: number) => {
    const date = task.createdDate ? parseISO(task.createdDate) : null;
    const initiator = task?.initiatorName ?? '';
    const displayRequestType = (task?.requestType && DisplayRequestType[task.requestType]) ?? '';
    const taskDisplayId = task?.taskNumber ?? '';
    const gsacId = task?.gsacId ?? '';
    const approver = task.ownerName || task.ownerId || task.groupId || '';
    return {
      key: `row-${index}`,
      cells: [
        {
          key: date ? date.valueOf() : 0,
          content: date
            ? withTooltip(
                <FormattedDate date={task.createdDate} showTime />,
                <div className="text-overflow">{formatDistanceToNow(date, { addSuffix: true })}</div>
              )
            : '',
          data: date ? formatDistanceToNow(date, { addSuffix: true }) : '',
        },
        {
          key: TABLE_COLUMN_KEYS.TASK_NUMBER + index,
          content: withTooltip(taskDisplayId, <TaskDetailAction task={task} refetch={refetch} refetchAll={refetch} />),
          data: taskDisplayId,
        },
        {
          key: TABLE_COLUMN_KEYS.GSAC_ID + index,
          content: withTooltip(gsacId, <div className="text-overflow">{gsacId}</div>),
          data: taskDisplayId,
        },
        {
          key: TABLE_COLUMN_KEYS.REQUEST_TYPE + index,
          content: withTooltip(displayRequestType, <div className="text-overflow">{displayRequestType}</div>),
          data: displayRequestType,
        },
        {
          key: TABLE_COLUMN_KEYS.INITIATOR + index,
          content: withTooltip(initiator, <div className="text-overflow">{initiator}</div>),
          data: initiator,
        },
        {
          key: TABLE_COLUMN_KEYS.APPROVER + index,
          content: withTooltip(approver, <div className="text-overflow">{approver}</div>),
          data: approver,
        },
        {
          key: TABLE_COLUMN_KEYS.STATUS + index,
          content: getApprovalStatusContent(task.approvalStatus),
          data:
            task.approvalStatus && statusToShow[task.approvalStatus]
              ? statusToShow[task.approvalStatus].toUpperCase()
              : '',
        },
        {
          key: TABLE_COLUMN_KEYS.ACTION + index,
          content: (
            <div className="action-btn">
              <TaskActionsList task={task} userRole={userRole} refetchAll={refetch} refetch={refetch} />
            </div>
          ),
        },
      ],
    };
  });
};
